import { Fragment, useEffect, useRef, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { Map as MapLibreMap, Marker } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import "@/lib/maplibreWorker";
import {
  AlertTriangle,
  Copy,
  CopyPlus,
  ImageIcon,
  KeyRound,
  Link as LinkIcon,
  ListOrdered,
  Loader2,
  NotebookPen,
  Plus,
  Sparkles,
  Trash2,
  TrafficCone,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { MAP_STYLE_URL, MAP_STYLE_CONFIG_ERROR, MAP_STYLE_LOAD_ERROR_MESSAGE } from "@/lib/mapStyle";

// Production domain of the separate StapKompas Verkeer app (verkeer-app/,
// documented in its own README) — used only to build a shareable "?event=..."
// deep link for a new/reset login code, so a controller only has to type the
// code (see verkeer-app/src/pages/Login.jsx's prefillEventId). This app
// (SVJ Beheer) never calls anything on that domain itself.
const VERKEER_APP_URL = "https://verkeer.studiovoorjou.nl";

// StapKompas Verkeer — admin management for traffic-controller accounts AND
// verkeersposten on a route. Routes themselves stay fully determined inside
// StapKompas (DaysManager/RouteBuilderModal); this page owns the entire,
// additive "Verkeer" concept layered on top of an existing route — placing,
// moving, deleting and assigning posts, and the live sluiter position —
// since placing a post here (not inside RouteBuilderModal's own "Verkeer"
// mode, which this replaces) is what keeps that capability from being
// buried inside route-building.
// Modeled on AdminLiveFlow.jsx's event -> day -> route drill-down and MapLibre
// construction recipe (worker import, style-URL handling, plain inner-div container).

const SLUITER_POLL_MS = 5000;
// Fase 3: a sluiter position older than this (server-computed age_seconds, see
// _tc_sluiter_location_response) likely means the app lost GPS/connectivity --
// shown as a clear alarm above the map rather than silently keeping the old dot.
const SLUITER_STALE_WARNING_SECONDS = 120;

export default function AdminTrafficControllers() {
  const { authAxios } = useAuth();

  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [loadingEvents, setLoadingEvents] = useState(true);

  const [controllers, setControllers] = useState([]);
  const [loadingControllers, setLoadingControllers] = useState(false);
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState("controller");
  const [newInstructions, setNewInstructions] = useState("");
  const [newLoginCode, setNewLoginCode] = useState("");
  const [creating, setCreating] = useState(false);
  const [newCode, setNewCode] = useState(null); // { controllerId, code } shown exactly once
  const [editingInstructionsFor, setEditingInstructionsFor] = useState(null); // controller id
  const [instructionsDraft, setInstructionsDraft] = useState("");
  const [savingInstructions, setSavingInstructions] = useState(false);
  const [resetCodeDraftFor, setResetCodeDraftFor] = useState(null); // controller id
  const [resetCodeDraft, setResetCodeDraft] = useState("");

  const [days, setDays] = useState([]);
  const [dayId, setDayId] = useState("");
  const [routes, setRoutes] = useState([]);
  const [routeId, setRouteId] = useState("");
  const [navData, setNavData] = useState(undefined);
  const [sluiter, setSluiter] = useState(null);
  const [trafficPosts, setTrafficPosts] = useState([]);
  const [mapError, setMapError] = useState(MAP_STYLE_CONFIG_ERROR);

  // Post placement/editing — moved here from RouteBuilderModal's old "Verkeer"
  // mode in full (not just the assignment dropdown): click the map to place a
  // new post, drag an existing one to move it, click one to edit/assign/delete
  // it, and detect likely crossings as suggestions.
  const [editorPost, setEditorPost] = useState(null);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [postSaving, setPostSaving] = useState(false);
  const [suggestBusy, setSuggestBusy] = useState(false);
  const [suggestDayBusy, setSuggestDayBusy] = useState(false);
  const [autoGenerateBusy, setAutoGenerateBusy] = useState(false);
  const [autoGenerateResult, setAutoGenerateResult] = useState(null);
  const [postImageUploading, setPostImageUploading] = useState(false);

  // Fase 1 vervolgronde: dupliceren van alle posten (incl. tijdvenster/foto,
  // nooit toewijzing/status) naar een andere dag/route — zodat een organisator
  // niet elke dag van een meerdaags evenement de hele postenlijst opnieuw moet
  // intekenen.
  const [duplicateTargetRouteId, setDuplicateTargetRouteId] = useState("");
  const [duplicating, setDuplicating] = useState(false);
  const [allRoutesForDuplicate, setAllRoutesForDuplicate] = useState([]);

  // Fase 2 (vervolgplan): carrousel -- regelaar-ketting van posten. One
  // controller can be chained to a chronological sequence of posts (post A
  // 18:00-19:00, post B 19:15-20:30, ...) instead of a single flat assignment.
  const [openKettingFor, setOpenKettingFor] = useState(null); // controller id
  const [assignmentsByController, setAssignmentsByController] = useState({});
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [assignmentDraftByController, setAssignmentDraftByController] = useState({});
  const [savingAssignmentFor, setSavingAssignmentFor] = useState(null);
  const [coverageGaps, setCoverageGaps] = useState([]);
  const [loadingCoverage, setLoadingCoverage] = useState(false);

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const sluiterMarkerRef = useRef(null);
  const postMarkersRef = useRef([]);
  // The MapLibre `click` handler is registered once inside the mount-only map
  // effect below, so it can't close over fresh state on every render (the exact
  // stale-closure shape that once cost the route builder every waypoint but the
  // last — see CURRENT-STATE.md). This ref always points at the latest
  // click-dispatch closure; the click handler calls through it, never a
  // captured copy.
  const handleMapClickRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await authAxios.get("/admin/events");
        if (cancelled) return;
        const items = (data.items || []).filter((e) => e.traffic_control_enabled);
        setEvents(items);
        setEventId((current) => (items.some((e) => e.id === current) ? current : items[0]?.id || ""));
      } catch (error) {
        if (!cancelled) toast.error(error?.response?.data?.detail || error.message);
      } finally {
        if (!cancelled) setLoadingEvents(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authAxios]);

  const loadControllers = async (eid) => {
    if (!eid) {
      setControllers([]);
      return;
    }
    setLoadingControllers(true);
    try {
      const { data } = await authAxios.get(`/admin/events/${eid}/traffic-controllers`);
      setControllers(data.items || []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || error.message);
    } finally {
      setLoadingControllers(false);
    }
  };

  useEffect(() => {
    loadControllers(eventId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  useEffect(() => {
    setDays([]);
    setDayId("");
    if (!eventId) return undefined;
    let cancelled = false;
    authAxios
      .get(`/admin/events/${eventId}/days`)
      .then(({ data }) => {
        if (cancelled) return;
        const items = data.items || [];
        setDays(items);
        setDayId(items[0]?.id || "");
      })
      .catch((error) => {
        if (!cancelled) toast.error(error?.response?.data?.detail || error.message);
      });
    return () => {
      cancelled = true;
    };
  }, [authAxios, eventId]);

  // Flat route list across every day of the event, for the "Dupliceer posten
  // naar..." target picker only — same pattern AdminTrafficChat.jsx already
  // uses for its route-channel picker, not a second route listing concept.
  useEffect(() => {
    setAllRoutesForDuplicate([]);
    if (!days.length) return undefined;
    let cancelled = false;
    Promise.all(days.map((d) => authAxios.get(`/admin/event-days/${d.id}/routes`).then((r) => r.data.items || [])))
      .then((lists) => {
        if (!cancelled) setAllRoutesForDuplicate(lists.flat());
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [authAxios, days]);

  useEffect(() => {
    setRoutes([]);
    setRouteId("");
    if (!dayId) return undefined;
    let cancelled = false;
    authAxios
      .get(`/admin/event-days/${dayId}/routes`)
      .then(({ data }) => {
        if (cancelled) return;
        const items = data.items || [];
        setRoutes(items);
        setRouteId(items[0]?.id || "");
      })
      .catch((error) => {
        if (!cancelled) toast.error(error?.response?.data?.detail || error.message);
      });
    return () => {
      cancelled = true;
    };
  }, [authAxios, dayId]);

  useEffect(() => {
    setNavData(undefined);
    setSluiter(null);
    setTrafficPosts([]);
    setEditorPost(null);
    setSelectedPostId(null);
    if (!routeId) return undefined;
    let cancelled = false;
    Promise.all([
      authAxios.get(`/admin/routes/${routeId}/navigation`),
      authAxios.get(`/admin/routes/${routeId}/traffic-posts`),
    ])
      .then(([navRes, postsRes]) => {
        if (cancelled) return;
        setNavData(navRes.data);
        setTrafficPosts(postsRes.data?.items || []);
      })
      .catch((error) => {
        if (!cancelled) toast.error(error?.response?.data?.detail || error.message);
      });
    return () => {
      cancelled = true;
    };
  }, [authAxios, routeId]);

  // Poll the sluiter's live position.
  useEffect(() => {
    if (!routeId) return undefined;
    let cancelled = false;
    const poll = () => {
      authAxios
        .get(`/admin/routes/${routeId}/sluiter-location`)
        .then(({ data }) => {
          if (!cancelled) setSluiter(data);
        })
        .catch(() => {});
    };
    poll();
    const timer = setInterval(poll, SLUITER_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [authAxios, routeId]);

  // Construct the map once per route.
  useEffect(() => {
    if (!navData?.geojson || !mapContainerRef.current) return undefined;
    if (MAP_STYLE_CONFIG_ERROR) {
      setMapError(MAP_STYLE_CONFIG_ERROR);
      return undefined;
    }
    setMapError(null);
    const coords = navData.geojson?.geometry?.coordinates || [];
    const center = coords[Math.floor(coords.length / 2)] || coords[0];
    const map = new MapLibreMap({
      container: mapContainerRef.current,
      style: MAP_STYLE_URL,
      center: center || [5.1214, 52.0907],
      zoom: center ? 14 : 6,
    });
    mapRef.current = map;
    map.on("error", () => setMapError(MAP_STYLE_LOAD_ERROR_MESSAGE));
    map.on("click", (e) => {
      handleMapClickRef.current?.(e.lngLat.lng, e.lngLat.lat);
    });

    const drawLine = () => {
      if (!mapRef.current || mapRef.current.getSource("tc-route-line")) return;
      mapRef.current.addSource("tc-route-line", { type: "geojson", data: navData.geojson });
      mapRef.current.addLayer({
        id: "tc-route-line",
        type: "line",
        source: "tc-route-line",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-width": 5, "line-color": "#e8630a" },
      });
    };
    if (map.isStyleLoaded()) drawLine();
    else map.on("load", drawLine);

    return () => {
      map.remove();
      mapRef.current = null;
      sluiterMarkerRef.current = null;
      postMarkersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navData?.geojson]);

  const closeEditor = () => {
    setEditorPost(null);
    setSelectedPostId(null);
  };

  const openEditorForNew = (lon, lat) => {
    setSelectedPostId(null);
    setEditorPost({
      id: null,
      title: "",
      notes: "",
      longitude: lon,
      latitude: lat,
      status: "confirmed",
      assigned_controller_id: null,
      active_from: "",
      active_until: "",
      image_url: null,
    });
  };

  const openEditorForExisting = (post) => {
    setSelectedPostId(post.id);
    setEditorPost({ ...post });
  };

  // Kept in sync on every render (no deps array) so the click handler registered
  // once in the mount-only map effect always dispatches to the current render's
  // closures, never a stale one captured at mount.
  useEffect(() => {
    handleMapClickRef.current = (lon, lat) => {
      openEditorForNew(lon, lat);
    };
  });

  const saveEditor = async () => {
    if (!editorPost || !editorPost.title.trim()) {
      toast.error("Titel is verplicht");
      return;
    }
    setPostSaving(true);
    const payload = {
      title: editorPost.title.trim(),
      notes: editorPost.notes || null,
      longitude: editorPost.longitude,
      latitude: editorPost.latitude,
      assigned_controller_id: editorPost.assigned_controller_id || null,
      status: editorPost.status,
      active_from: editorPost.active_from || null,
      active_until: editorPost.active_until || null,
    };
    try {
      let data;
      if (editorPost.id) {
        ({ data } = await authAxios.put(`/admin/routes/${routeId}/traffic-posts/${editorPost.id}`, payload));
        setTrafficPosts((prev) => prev.map((p) => (p.id === editorPost.id ? data : p)));
        toast.success("Verkeerspost bijgewerkt");
      } else {
        ({ data } = await authAxios.post(`/admin/routes/${routeId}/traffic-posts`, payload));
        setTrafficPosts((prev) => [...prev, data]);
        toast.success("Verkeerspost toegevoegd");
      }
      if (data.overlap_warning) toast.warning(data.overlap_warning);
      closeEditor();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Opslaan van verkeerspost mislukt");
    } finally {
      setPostSaving(false);
    }
  };

  const uploadPostImage = async (file) => {
    if (!editorPost?.id || !file) return;
    setPostImageUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await authAxios.post(`/admin/routes/${routeId}/traffic-posts/${editorPost.id}/image`, form);
      setEditorPost((prev) => (prev ? { ...prev, image_url: data.image_url } : prev));
      setTrafficPosts((prev) => prev.map((p) => (p.id === editorPost.id ? { ...p, image_url: data.image_url } : p)));
      toast.success("Foto geüpload");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Uploaden van foto mislukt");
    } finally {
      setPostImageUploading(false);
    }
  };

  const removePostImage = async () => {
    if (!editorPost?.id) return;
    setPostImageUploading(true);
    try {
      await authAxios.delete(`/admin/routes/${routeId}/traffic-posts/${editorPost.id}/image`);
      setEditorPost((prev) => (prev ? { ...prev, image_url: null } : prev));
      setTrafficPosts((prev) => prev.map((p) => (p.id === editorPost.id ? { ...p, image_url: null } : p)));
      toast.success("Foto verwijderd");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Verwijderen van foto mislukt");
    } finally {
      setPostImageUploading(false);
    }
  };

  const duplicatePosts = async () => {
    if (!duplicateTargetRouteId || !routeId) return;
    if (!window.confirm("Alle posten van deze route (met tijdvenster/foto, zonder toewijzing) kopiëren naar de gekozen route?")) {
      return;
    }
    setDuplicating(true);
    try {
      const { data } = await authAxios.post(`/admin/routes/${routeId}/traffic-posts/duplicate`, {
        target_route_id: duplicateTargetRouteId,
      });
      toast.success(`${data.count} post(en) gekopieerd`);
      if (duplicateTargetRouteId === routeId) {
        setTrafficPosts((prev) => [...prev, ...(data.items || [])]);
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Dupliceren mislukt");
    } finally {
      setDuplicating(false);
    }
  };

  const deleteEditor = async () => {
    if (!editorPost?.id) {
      closeEditor();
      return;
    }
    if (!window.confirm("Deze verkeerspost verwijderen?")) return;
    setPostSaving(true);
    try {
      await authAxios.delete(`/admin/routes/${routeId}/traffic-posts/${editorPost.id}`);
      setTrafficPosts((prev) => prev.filter((p) => p.id !== editorPost.id));
      closeEditor();
      toast.success("Verkeerspost verwijderd");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Verwijderen mislukt");
    } finally {
      setPostSaving(false);
    }
  };

  const confirmSuggestion = async () => {
    if (!editorPost?.id) return;
    setPostSaving(true);
    try {
      const { data } = await authAxios.put(`/admin/routes/${routeId}/traffic-posts/${editorPost.id}`, {
        status: "confirmed",
      });
      setTrafficPosts((prev) => prev.map((p) => (p.id === editorPost.id ? data : p)));
      setEditorPost((prev) => (prev ? { ...prev, status: "confirmed" } : prev));
      toast.success("Suggestie bevestigd");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Bevestigen mislukt");
    } finally {
      setPostSaving(false);
    }
  };

  const movePostOnDrag = async (post, lon, lat) => {
    try {
      const { data } = await authAxios.put(`/admin/routes/${routeId}/traffic-posts/${post.id}`, {
        longitude: lon,
        latitude: lat,
      });
      setTrafficPosts((prev) => prev.map((p) => (p.id === post.id ? data : p)));
      setEditorPost((prev) => (prev && prev.id === post.id ? { ...prev, longitude: lon, latitude: lat } : prev));
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Verplaatsen mislukt");
    }
  };

  const detectCrossings = async () => {
    setSuggestBusy(true);
    try {
      const { data } = await authAxios.post(`/admin/routes/${routeId}/traffic-posts/suggest`);
      setTrafficPosts((prev) => [...prev, ...(data.items || [])]);
      const osmNote = data.osm_data_used
        ? " (incl. live wegdata)"
        : " (live wegdata niet beschikbaar, alleen op basis van de routevorm)";
      if ((data.count || 0) === 0) {
        toast.info(`Geen duidelijke kruisingen gevonden op deze route${osmNote}`);
      } else {
        toast.success(`${data.count} suggestie(s) toegevoegd${osmNote}`);
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Detecteren van kruisingen mislukt");
    } finally {
      setSuggestBusy(false);
    }
  };

  // Runs the same heuristic across EVERY route of the current day at once (e.g.
  // a 5 km and a 10 km distance on the same evening), so an organiser doesn't
  // have to open each route's own map and click "Detecteer kruisingen"
  // separately. Only this route's own suggestions are merged into trafficPosts
  // here — a sibling route's suggestions still land correctly in its own
  // event_route_traffic_posts rows, just not visible until that route is opened.
  const detectCrossingsForDay = async () => {
    if (!dayId) return;
    setSuggestDayBusy(true);
    try {
      const { data } = await authAxios.post(`/admin/event-days/${dayId}/traffic-posts/suggest`);
      const ownItems = (data.by_route && data.by_route[routeId] && data.by_route[routeId].items) || [];
      if (ownItems.length > 0) setTrafficPosts((prev) => [...prev, ...ownItems]);
      const total = data.total_count || 0;
      const osmNote = data.osm_data_used
        ? " (incl. live wegdata)"
        : " (live wegdata niet beschikbaar, alleen op basis van de routevorm)";
      if (total === 0) {
        toast.info(`Geen duidelijke kruisingen gevonden op de routes van deze dag${osmNote}`);
      } else {
        const perRoute = Object.values(data.by_route || {})
          .map((r) => `${r.title}: ${r.count}`)
          .join(", ");
        toast.success(`${total} suggestie(s) toegevoegd over alle routes van deze dag${osmNote} (${perRoute})`);
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Detecteren van kruisingen voor deze dag mislukt");
    } finally {
      setSuggestDayBusy(false);
    }
  };

  // Greedily fills every coverage gap on this route with a free controller --
  // "de meeste posten worden gewoon zoveel mogelijk bemand". Never overwrites an
  // existing manual assignment (the backend only ever fills real gaps), so this
  // is always safe to run again after adding posts/controllers.
  const autoGenerateKetting = async () => {
    if (!routeId) return;
    setAutoGenerateBusy(true);
    setAutoGenerateResult(null);
    try {
      const { data } = await authAxios.post(`/admin/routes/${routeId}/traffic-assignments/auto-generate`);
      setAutoGenerateResult(data);
      setAssignmentsByController({});
      loadCoverage(routeId);
      const createdCount = (data.created || []).length;
      const unassignedCount = (data.unassigned || []).length;
      if (createdCount === 0 && unassignedCount === 0) {
        toast.info("Niets om toe te wijzen — alle posten zijn al bemand of hebben geen tijdvenster");
      } else if (unassignedCount === 0) {
        toast.success(`${createdCount} koppeling(en) automatisch aangemaakt`);
      } else {
        toast.warning(`${createdCount} koppeling(en) aangemaakt, ${unassignedCount} post(en) blijven onbemand`);
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Automatisch inplannen mislukt");
    } finally {
      setAutoGenerateBusy(false);
    }
  };

  // Keep the post markers in sync — always interactive here (this page IS the
  // post-editing surface now). The unsaved draft (if any) renders as an extra
  // marker, same convention as RouteBuilderModal's own point/waypoint drafts.
  const draftKey = editorPost && !editorPost.id ? `${editorPost.longitude}:${editorPost.latitude}` : null;
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      postMarkersRef.current.forEach((m) => m.remove());
      const draft = editorPost && !editorPost.id ? editorPost : null;
      const allPosts = draft ? [...trafficPosts, draft] : trafficPosts;
      postMarkersRef.current = allPosts.map((post) => {
        const isDraft = post === draft;
        const selected = isDraft || selectedPostId === post.id;
        const isSuggested = post.status === "suggested";
        const el = document.createElement("div");
        el.setAttribute("data-testid", isDraft ? "traffic-post-draft" : `traffic-post-marker-${post.id}`);
        el.title = post.title || "Verkeerspost";
        el.style.width = "22px";
        el.style.height = "22px";
        el.style.borderRadius = "9999px";
        el.style.border = selected ? "3px solid #06b6d4" : isSuggested ? "2px dashed white" : "2px solid white";
        el.style.boxShadow = "0 1px 4px rgba(0,0,0,0.4)";
        el.style.background = isSuggested ? "#f59e0b" : "#0ea5e9";
        el.style.display = "flex";
        el.style.alignItems = "center";
        el.style.justifyContent = "center";
        el.style.cursor = "pointer";
        el.innerHTML = renderToStaticMarkup(<TrafficCone color="#ffffff" size={12} strokeWidth={2.5} />);
        const marker = new Marker({ element: el, draggable: true }).setLngLat([post.longitude, post.latitude]).addTo(map);
        marker.on("dragend", () => {
          const { lng, lat } = marker.getLngLat();
          if (isDraft) {
            setEditorPost((prev) => (prev ? { ...prev, longitude: lng, latitude: lat } : prev));
          } else {
            movePostOnDrag(post, lng, lat);
          }
        });
        el.addEventListener("click", (ev) => {
          ev.stopPropagation();
          if (!isDraft) openEditorForExisting(post);
        });
        return marker;
        // eslint-disable-next-line react-hooks/exhaustive-deps
      });
    };
    if (map.isStyleLoaded()) apply();
    else map.once("load", apply);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trafficPosts, navData, selectedPostId, draftKey]);

  // Move the sluiter marker on every poll — never re-render the line for this.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      if (!sluiter?.available) {
        if (sluiterMarkerRef.current) {
          sluiterMarkerRef.current.remove();
          sluiterMarkerRef.current = null;
        }
        return;
      }
      if (!sluiterMarkerRef.current) {
        const el = document.createElement("div");
        el.style.width = "18px";
        el.style.height = "18px";
        el.style.borderRadius = "9999px";
        el.style.border = "3px solid white";
        el.style.background = "#dc2626";
        el.style.boxShadow = "0 0 0 4px rgba(220,38,38,0.25)";
        el.title = "Sluiter (laatste loper)";
        sluiterMarkerRef.current = new Marker({ element: el }).addTo(map);
      }
      sluiterMarkerRef.current.setLngLat([sluiter.longitude, sluiter.latitude]);
    };
    if (map.isStyleLoaded()) apply();
    else map.once("load", apply);
  }, [sluiter]);

  const createController = async (e) => {
    e.preventDefault();
    if (!newName.trim() || !eventId) return;
    setCreating(true);
    try {
      const { data } = await authAxios.post(`/admin/events/${eventId}/traffic-controllers`, {
        display_name: newName.trim(),
        role: newRole,
        route_id: routeId || null,
        instructions: newInstructions.trim() || null,
        login_code: newLoginCode.trim() || null,
      });
      setNewCode({ controllerId: data.id, code: data.login_code, displayName: data.display_name });
      setNewName("");
      setNewInstructions("");
      setNewLoginCode("");
      loadControllers(eventId);
    } catch (error) {
      toast.error(error?.response?.data?.detail || error.message);
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (controller) => {
    try {
      await authAxios.patch(`/admin/traffic-controllers/${controller.id}`, { active: !controller.active });
      loadControllers(eventId);
    } catch (error) {
      toast.error(error?.response?.data?.detail || error.message);
    }
  };

  const resetCode = async (controller, customCode) => {
    try {
      const { data } = await authAxios.post(`/admin/traffic-controllers/${controller.id}/reset-code`, {
        login_code: customCode ? customCode.trim() : null,
      });
      setNewCode({ controllerId: controller.id, code: data.login_code, displayName: controller.display_name });
      setResetCodeDraftFor(null);
      setResetCodeDraft("");
    } catch (error) {
      toast.error(error?.response?.data?.detail || error.message);
    }
  };

  const openInstructionsEditor = (controller) => {
    setEditingInstructionsFor(controller.id);
    setInstructionsDraft(controller.instructions || "");
  };

  const saveInstructions = async (controller) => {
    setSavingInstructions(true);
    try {
      await authAxios.patch(`/admin/traffic-controllers/${controller.id}`, {
        instructions: instructionsDraft.trim() || null,
      });
      setEditingInstructionsFor(null);
      loadControllers(eventId);
    } catch (error) {
      toast.error(error?.response?.data?.detail || error.message);
    } finally {
      setSavingInstructions(false);
    }
  };

  const assignPostController = async (post, controllerId) => {
    try {
      const { data } = await authAxios.put(`/admin/routes/${routeId}/traffic-posts/${post.id}`, {
        assigned_controller_id: controllerId || null,
      });
      setTrafficPosts((prev) => prev.map((p) => (p.id === post.id ? data : p)));
      setEditorPost((prev) => (prev && prev.id === post.id ? { ...prev, assigned_controller_id: controllerId || null } : prev));
    } catch (error) {
      toast.error(error?.response?.data?.detail || error.message);
    }
  };

  const deleteController = async (controller) => {
    if (!window.confirm(`Account "${controller.display_name}" verwijderen?`)) return;
    try {
      await authAxios.delete(`/admin/traffic-controllers/${controller.id}`);
      loadControllers(eventId);
    } catch (error) {
      toast.error(error?.response?.data?.detail || error.message);
    }
  };

  // --- Fase 2: carrousel -- regelaar-ketting van posten -----------------------

  const loadCoverage = async (rid) => {
    if (!rid) {
      setCoverageGaps([]);
      return;
    }
    setLoadingCoverage(true);
    try {
      const { data } = await authAxios.get(`/admin/routes/${rid}/traffic-coverage`);
      setCoverageGaps(data.gaps || []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || error.message);
    } finally {
      setLoadingCoverage(false);
    }
  };

  useEffect(() => {
    loadCoverage(routeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId, trafficPosts]);

  const loadAssignments = async (controllerId) => {
    setLoadingAssignments(true);
    try {
      const { data } = await authAxios.get(`/admin/traffic-controllers/${controllerId}/assignments`);
      setAssignmentsByController((prev) => ({ ...prev, [controllerId]: data.items || [] }));
    } catch (error) {
      toast.error(error?.response?.data?.detail || error.message);
    } finally {
      setLoadingAssignments(false);
    }
  };

  const toggleKetting = (controller) => {
    if (openKettingFor === controller.id) {
      setOpenKettingFor(null);
      return;
    }
    setOpenKettingFor(controller.id);
    if (!assignmentsByController[controller.id]) loadAssignments(controller.id);
    if (!assignmentDraftByController[controller.id]) {
      setAssignmentDraftByController((prev) => ({
        ...prev,
        [controller.id]: { post_id: "", active_from: "", active_until: "" },
      }));
    }
  };

  const setAssignmentDraftField = (controllerId, field, value) => {
    setAssignmentDraftByController((prev) => ({
      ...prev,
      [controllerId]: { ...(prev[controllerId] || {}), [field]: value },
    }));
  };

  // After any assignment create/update/delete, the cached assigned_controller_id
  // on the affected post(s) may have changed server-side — re-fetch the post list
  // for this route so "Posten op deze route" and the coverage report both reflect
  // the fresh cache, rather than mutating trafficPosts by hand in several places.
  const reloadTrafficPosts = async () => {
    if (!routeId) return;
    try {
      const { data } = await authAxios.get(`/admin/routes/${routeId}/traffic-posts`);
      setTrafficPosts(data.items || []);
    } catch {
      // Best-effort refresh — a failure here isn't worth surfacing on top of
      // whatever the calling action already reported.
    }
  };

  const addAssignment = async (controllerId) => {
    const draft = assignmentDraftByController[controllerId] || {};
    if (!draft.post_id || !draft.active_from || !draft.active_until) {
      toast.error("Post, van-tijd en tot-tijd zijn verplicht");
      return;
    }
    setSavingAssignmentFor(controllerId);
    try {
      const { data } = await authAxios.post(`/admin/traffic-controllers/${controllerId}/assignments`, {
        post_id: draft.post_id,
        active_from: draft.active_from,
        active_until: draft.active_until,
      });
      setAssignmentsByController((prev) => ({
        ...prev,
        [controllerId]: [...(prev[controllerId] || []), data].sort(
          (a, b) => a.sequence_order - b.sequence_order || a.active_from.localeCompare(b.active_from),
        ),
      }));
      setAssignmentDraftByController((prev) => ({ ...prev, [controllerId]: { post_id: "", active_from: "", active_until: "" } }));
      toast.success("Koppeling toegevoegd");
      if (data.transition_warning) toast.warning(data.transition_warning);
      reloadTrafficPosts();
      loadCoverage(routeId);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Koppeling toevoegen mislukt");
    } finally {
      setSavingAssignmentFor(null);
    }
  };

  const deleteAssignment = async (controllerId, assignmentId) => {
    if (!window.confirm("Deze koppeling verwijderen?")) return;
    try {
      await authAxios.delete(`/admin/traffic-assignments/${assignmentId}`);
      setAssignmentsByController((prev) => ({
        ...prev,
        [controllerId]: (prev[controllerId] || []).filter((a) => a.id !== assignmentId),
      }));
      reloadTrafficPosts();
      loadCoverage(routeId);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Verwijderen mislukt");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code).then(
      () => toast.success("Code gekopieerd"),
      () => {},
    );
  };

  const loginLinkFor = (eid) => `${VERKEER_APP_URL}/?event=${encodeURIComponent(eid)}`;

  const copyLoginMessage = (eid, code, name) => {
    const message = `StapKompas Verkeer — inloggegevens voor ${name}:\nLink: ${loginLinkFor(eid)}\nCode: ${code}`;
    navigator.clipboard?.writeText(message).then(
      () => toast.success("Link + code gekopieerd"),
      () => {},
    );
  };

  return (
    <div data-testid="admin-traffic-control-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 flex items-center justify-center">
          <TrafficCone className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold text-white">Verkeersregelaars</h1>
          <p className="text-sm text-slate-400">
            StapKompas Verkeer — accounts, verkeersposten en de live sluiter-positie. Routes blijven bepaald in
            StapKompas zelf (Dagen &amp; Routes); verkeersposten plaats, verplaats en wijs je hier toe.
          </p>
        </div>
      </div>

      {loadingEvents && (
        <div className="rounded-2xl border border-svj-border bg-svj-card p-6 text-sm text-slate-400 inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Events laden…
        </div>
      )}

      {!loadingEvents && events.length === 0 && (
        <div className="rounded-2xl border border-svj-border bg-svj-card p-6 text-sm text-slate-400">
          Er is nog geen event waarvoor StapKompas Verkeer actief is. Zet "StapKompas Verkeer" aan bij een event
          (tabblad Instellingen) om deze pagina te gebruiken.
        </div>
      )}

      {!loadingEvents && events.length > 0 && (
        <div className="space-y-6">
          {events.length > 1 && (
            <label className="block max-w-xs">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Event</span>
              <select
                value={eventId}
                onChange={(e) => setEventId(e.target.value)}
                data-testid="traffic-control-event-select"
                className="mt-1 w-full h-11 px-3 rounded-lg bg-svj-card border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan"
              >
                {events.map((event) => (
                  <option key={event.id} value={event.id}>{event.title}</option>
                ))}
              </select>
            </label>
          )}

          <div className="rounded-2xl border border-svj-border bg-svj-card p-5">
            <h2 className="text-sm font-bold text-white mb-3">Accounts</h2>
            <form onSubmit={createController} className="flex flex-wrap items-end gap-2 mb-4">
              <label className="block">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Naam</span>
                <input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  data-testid="traffic-controller-new-name"
                  className="mt-1 h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm"
                />
              </label>
              <label className="block">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Rol</span>
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  data-testid="traffic-controller-new-role"
                  className="mt-1 h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm"
                >
                  <option value="controller">Verkeersregelaar</option>
                  <option value="sluiter">Sluiter (laatste loper)</option>
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                  Inlogcode (optioneel)
                </span>
                <input
                  value={newLoginCode}
                  onChange={(e) => setNewLoginCode(e.target.value)}
                  placeholder="leeg = willekeurig"
                  data-testid="traffic-controller-new-code-input"
                  className="mt-1 h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm w-40"
                />
              </label>
              <label className="block w-full">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                  Instructies voor de hele dag (optioneel)
                </span>
                <textarea
                  value={newInstructions}
                  onChange={(e) => setNewInstructions(e.target.value)}
                  rows={2}
                  placeholder="Bijv. standplaats, looptijden, bijzonderheden voor deze dienst"
                  data-testid="traffic-controller-new-instructions"
                  className="mt-1 w-full px-3 py-2 rounded-lg bg-svj-bg border border-svj-border text-white text-sm resize-none"
                />
              </label>
              <p className="w-full text-[11px] text-slate-400 -mt-1">
                Zelf een code gekozen (bijv. "FIETS01")? Dan ken je die zelf al en kun je
                hem altijd opnieuw delen — een willekeurige code wordt maar één keer getoond.
              </p>
              <button
                type="submit"
                disabled={creating || !newName.trim()}
                data-testid="traffic-controller-create"
                className="h-9 px-3 rounded-lg bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
              >
                {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                Account aanmaken
              </button>
            </form>

            {newCode && (
              <div
                className="mb-4 rounded-lg border border-svj-cyan/50 bg-svj-cyan/10 px-3 py-3 space-y-2"
                data-testid="traffic-controller-new-code"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs text-white">
                    Inlogcode (eenmalig getoond — geef deze door aan de regelaar):{" "}
                    <span className="font-mono font-bold text-base tracking-widest">{newCode.code}</span>
                  </span>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => copyCode(newCode.code)} className="text-slate-300 hover:text-white">
                      <Copy className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => setNewCode(null)} className="text-xs text-slate-400 hover:text-white">
                      Sluiten
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-3 border-t border-svj-cyan/20 pt-2">
                  <span className="text-xs text-slate-300 truncate" title={loginLinkFor(eventId)}>
                    <LinkIcon className="h-3 w-3 inline mr-1 -mt-0.5" />
                    {loginLinkFor(eventId)}
                  </span>
                  <button
                    type="button"
                    onClick={() => copyLoginMessage(eventId, newCode.code, newCode.displayName)}
                    data-testid="traffic-controller-copy-login-message"
                    className="shrink-0 text-xs font-semibold text-svj-cyan hover:text-white whitespace-nowrap"
                  >
                    Link + code kopiëren
                  </button>
                </div>
                <p className="text-[11px] text-slate-400">
                  Stuur deze link naar de regelaar — de app vult het event-ID dan vast in, hij hoeft alleen de code
                  nog in te typen.
                </p>
              </div>
            )}

            {loadingControllers ? (
              <div className="text-sm text-slate-400 inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Laden…
              </div>
            ) : controllers.length === 0 ? (
              <p className="text-sm text-slate-400">Nog geen accounts aangemaakt.</p>
            ) : (
              <table className="w-full text-sm" data-testid="traffic-controllers-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                    <th className="pb-2">Naam</th>
                    <th className="pb-2">Rol</th>
                    <th className="pb-2">Route</th>
                    <th className="pb-2">Status</th>
                    <th className="pb-2" />
                  </tr>
                </thead>
                <tbody>
                  {controllers.map((c) => (
                    <Fragment key={c.id}>
                      <tr className="border-t border-svj-border" data-testid={`traffic-controller-row-${c.id}`}>
                        <td className="py-2 text-white font-semibold align-top">
                          {c.display_name}
                          {c.instructions && editingInstructionsFor !== c.id && (
                            <p className="mt-0.5 text-[11px] font-normal text-slate-400 whitespace-pre-line">
                              {c.instructions}
                            </p>
                          )}
                        </td>
                        <td className="py-2 text-slate-300 align-top">
                          {c.role === "sluiter" ? "Sluiter" : "Verkeersregelaar"}
                        </td>
                        <td className="py-2 text-slate-400 align-top">
                          {routes.find((r) => r.id === c.route_id)?.title || (c.route_id ? c.route_id : "—")}
                        </td>
                        <td className="py-2 align-top">
                          <span className={c.active ? "text-a4d-green" : "text-slate-500"}>
                            {c.active ? "Actief" : "Inactief"}
                          </span>
                        </td>
                        <td className="py-2 text-right whitespace-nowrap align-top">
                          <button
                            type="button"
                            onClick={() => toggleKetting(c)}
                            title="Ketting van posten (carrousel)"
                            data-testid={`traffic-controller-toggle-ketting-${c.id}`}
                            className={`mr-3 inline-flex items-center ${openKettingFor === c.id ? "text-svj-cyan" : "text-slate-300"} hover:text-white`}
                          >
                            <ListOrdered className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => openInstructionsEditor(c)}
                            title="Instructies voor de hele dag bewerken"
                            data-testid={`traffic-controller-edit-instructions-${c.id}`}
                            className={`mr-3 inline-flex items-center ${c.instructions ? "text-svj-cyan" : "text-slate-300"} hover:text-white`}
                          >
                            <NotebookPen className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleActive(c)}
                            className="text-xs text-slate-300 hover:text-white mr-3"
                          >
                            {c.active ? "Deactiveren" : "Activeren"}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setResetCodeDraftFor(resetCodeDraftFor === c.id ? null : c.id);
                              setResetCodeDraft("");
                            }}
                            title="Inlogcode opnieuw instellen of zelf kiezen"
                            data-testid={`traffic-controller-reset-code-toggle-${c.id}`}
                            className={`mr-3 inline-flex items-center ${resetCodeDraftFor === c.id ? "text-svj-cyan" : "text-slate-300"} hover:text-white`}
                          >
                            <KeyRound className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteController(c)}
                            title="Verwijderen"
                            className="text-slate-300 hover:text-red-400 inline-flex items-center"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                      {editingInstructionsFor === c.id && (
                        <tr className="border-t border-svj-border" data-testid={`traffic-controller-instructions-editor-${c.id}`}>
                          <td colSpan={5} className="py-2">
                            <div className="rounded-lg border border-svj-cyan/40 bg-svj-cyan/5 p-3 space-y-2">
                              <textarea
                                value={instructionsDraft}
                                onChange={(e) => setInstructionsDraft(e.target.value)}
                                rows={3}
                                placeholder="Instructies voor de hele dag — zichtbaar voor deze regelaar/sluiter in de app"
                                data-testid={`traffic-controller-instructions-input-${c.id}`}
                                className="w-full px-3 py-2 rounded-lg bg-svj-bg border border-svj-border text-white text-sm resize-none"
                              />
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => saveInstructions(c)}
                                  disabled={savingInstructions}
                                  data-testid={`traffic-controller-instructions-save-${c.id}`}
                                  className="h-8 px-3 rounded-lg bg-svj-cyan text-svj-bg font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
                                >
                                  {savingInstructions && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                                  Opslaan
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEditingInstructionsFor(null)}
                                  className="h-8 px-3 rounded-lg text-xs text-slate-300 hover:text-white"
                                >
                                  Annuleren
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      {resetCodeDraftFor === c.id && (
                        <tr className="border-t border-svj-border" data-testid={`traffic-controller-reset-code-editor-${c.id}`}>
                          <td colSpan={5} className="py-2">
                            <div className="rounded-lg border border-svj-cyan/40 bg-svj-cyan/5 p-3 space-y-2">
                              <p className="text-[11px] text-slate-400">
                                Kies zelf een nieuwe code (bijv. "FIETS01") om die altijd opnieuw te kunnen delen, of
                                laat leeg voor een willekeurige code — die wordt maar één keer getoond.
                              </p>
                              <input
                                value={resetCodeDraft}
                                onChange={(e) => setResetCodeDraft(e.target.value)}
                                placeholder="leeg = willekeurig"
                                data-testid={`traffic-controller-reset-code-input-${c.id}`}
                                className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm w-48"
                              />
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => resetCode(c, resetCodeDraft)}
                                  data-testid={`traffic-controller-reset-code-confirm-${c.id}`}
                                  className="h-8 px-3 rounded-lg bg-svj-cyan text-svj-bg font-bold text-xs"
                                >
                                  Code instellen
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setResetCodeDraftFor(null);
                                    setResetCodeDraft("");
                                  }}
                                  className="h-8 px-3 rounded-lg text-xs text-slate-300 hover:text-white"
                                >
                                  Annuleren
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      {openKettingFor === c.id && (
                        <tr className="border-t border-svj-border" data-testid={`traffic-controller-ketting-${c.id}`}>
                          <td colSpan={5} className="py-2">
                            <div className="rounded-lg border border-svj-border bg-svj-bg p-3 space-y-2">
                              <h3 className="text-xs font-bold text-white">Ketting van posten</h3>
                              <p className="text-[11px] text-slate-400">
                                Koppel deze regelaar aan een reeks posten met een echt tijdvenster per post — zodat
                                de "van wissel-post naar volgende wissel-post"-melding altijd naar de juiste persoon
                                gaat.
                              </p>
                              {loadingAssignments && !assignmentsByController[c.id] ? (
                                <div className="text-xs text-slate-400 inline-flex items-center gap-2">
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Laden…
                                </div>
                              ) : (assignmentsByController[c.id] || []).length === 0 ? (
                                <p className="text-xs text-slate-500">Nog geen koppelingen.</p>
                              ) : (
                                <ol className="space-y-1">
                                  {(assignmentsByController[c.id] || []).map((a, idx) => {
                                    const post = trafficPosts.find((p) => p.id === a.post_id);
                                    return (
                                      <li
                                        key={a.id}
                                        data-testid={`traffic-assignment-row-${a.id}`}
                                        className="flex items-center justify-between gap-2 rounded-lg border border-svj-border bg-svj-card px-2 py-1.5 text-xs"
                                      >
                                        <span className="text-slate-300">
                                          <span className="text-slate-500">{idx + 1}.</span>{" "}
                                          <span className="text-white font-semibold">{post?.title || a.post_id}</span>{" "}
                                          <span className="text-slate-400">{a.active_from}–{a.active_until}</span>
                                        </span>
                                        <button
                                          type="button"
                                          onClick={() => deleteAssignment(c.id, a.id)}
                                          title="Verwijderen"
                                          data-testid={`traffic-assignment-delete-${a.id}`}
                                          className="text-slate-400 hover:text-red-400 inline-flex items-center"
                                        >
                                          <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                      </li>
                                    );
                                  })}
                                </ol>
                              )}
                              <div className="flex flex-wrap items-end gap-2 pt-1">
                                <select
                                  value={(assignmentDraftByController[c.id] || {}).post_id || ""}
                                  onChange={(e) => setAssignmentDraftField(c.id, "post_id", e.target.value)}
                                  data-testid={`traffic-assignment-post-select-${c.id}`}
                                  className="h-8 px-2 rounded-lg bg-svj-card border border-svj-border text-white text-xs min-w-[140px]"
                                >
                                  <option value="">— Post —</option>
                                  {trafficPosts
                                    .filter((p) => p.route_id === c.route_id || !p.route_id)
                                    .map((p) => (
                                      <option key={p.id} value={p.id}>{p.title}</option>
                                    ))}
                                </select>
                                <input
                                  type="time"
                                  value={(assignmentDraftByController[c.id] || {}).active_from || ""}
                                  onChange={(e) => setAssignmentDraftField(c.id, "active_from", e.target.value)}
                                  data-testid={`traffic-assignment-from-${c.id}`}
                                  className="h-8 px-2 rounded-lg bg-svj-card border border-svj-border text-white text-xs"
                                />
                                <input
                                  type="time"
                                  value={(assignmentDraftByController[c.id] || {}).active_until || ""}
                                  onChange={(e) => setAssignmentDraftField(c.id, "active_until", e.target.value)}
                                  data-testid={`traffic-assignment-until-${c.id}`}
                                  className="h-8 px-2 rounded-lg bg-svj-card border border-svj-border text-white text-xs"
                                />
                                <button
                                  type="button"
                                  onClick={() => addAssignment(c.id)}
                                  disabled={savingAssignmentFor === c.id}
                                  data-testid={`traffic-assignment-add-${c.id}`}
                                  className="h-8 px-3 rounded-lg bg-svj-cyan text-svj-bg font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
                                >
                                  {savingAssignmentFor === c.id && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                                  Toevoegen
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {routeId && (
            <div className="rounded-2xl border border-svj-border bg-svj-card p-5">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h2 className="text-sm font-bold text-white">Ketting automatisch genereren</h2>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Vult elke dekkingshiaat op deze route met een vrije regelaar — "zoveel mogelijk bemand", geen
                    streven naar een minimum aantal regelaars. Vult nooit een al handmatig ingevulde koppeling over.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={autoGenerateKetting}
                  disabled={autoGenerateBusy}
                  data-testid="traffic-auto-generate-assignments"
                  className="h-9 px-3 rounded-lg bg-svj-cyan text-svj-navy text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-40 shrink-0"
                >
                  {autoGenerateBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  Genereer ketting automatisch
                </button>
              </div>
              {autoGenerateResult && (
                <div className="mt-3 text-xs space-y-1" data-testid="traffic-auto-generate-result">
                  <p className="text-emerald-300">
                    {(autoGenerateResult.created || []).length} koppeling(en) aangemaakt.
                  </p>
                  {(autoGenerateResult.skipped || []).length > 0 && (
                    <p className="text-slate-400">
                      {(autoGenerateResult.skipped || []).length} post(en) overgeslagen (geen tijdvenster).
                    </p>
                  )}
                  {(autoGenerateResult.unassigned || []).length > 0 && (
                    <div className="text-amber-300">
                      <p>{(autoGenerateResult.unassigned || []).length} post(en) onbemand gebleven:</p>
                      <ul className="list-disc list-inside mt-0.5">
                        {autoGenerateResult.unassigned.map((u, idx) => (
                          <li key={`${u.post_id}-${idx}`}>
                            {u.title} ({u.active_from}–{u.active_until})
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {routeId && coverageGaps.length > 0 && (
            <div className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-5" data-testid="traffic-coverage-panel">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-amber-400" />
                <h2 className="text-sm font-bold text-amber-300">Dekking — mogelijke gaten</h2>
                {loadingCoverage && <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-400" />}
              </div>
              <p className="text-[11px] text-slate-400 mb-3">
                Deze posten hebben een tijdvenster, maar niet voor elk moment in dat venster een actieve koppeling —
                puur een waarschuwing, de organisatie kan een post bewust onbemand laten.
              </p>
              <ul className="space-y-1">
                {coverageGaps.map((gap, idx) => (
                  <li
                    key={`${gap.post_id}-${gap.uncovered_from}-${idx}`}
                    data-testid={`traffic-coverage-gap-${gap.post_id}`}
                    className="text-xs text-amber-200"
                  >
                    <span className="font-semibold">{gap.title}</span>: geen koppeling van {gap.uncovered_from} tot{" "}
                    {gap.uncovered_until} (venster {gap.active_from}–{gap.active_until})
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap gap-4">
            {days.length > 1 && (
              <label className="block max-w-xs flex-1 min-w-[160px]">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Dag</span>
                <select
                  value={dayId}
                  onChange={(e) => setDayId(e.target.value)}
                  data-testid="traffic-control-day-select"
                  className="mt-1 w-full h-11 px-3 rounded-lg bg-svj-card border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan"
                >
                  {days.map((day) => (
                    <option key={day.id} value={day.id}>{day.label || `Dag ${day.day_number}`}</option>
                  ))}
                </select>
              </label>
            )}
            {routes.length > 1 && (
              <label className="block max-w-xs flex-1 min-w-[160px]">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Route</span>
                <select
                  value={routeId}
                  onChange={(e) => setRouteId(e.target.value)}
                  data-testid="traffic-control-route-select"
                  className="mt-1 w-full h-11 px-3 rounded-lg bg-svj-card border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan"
                >
                  {routes.map((route) => (
                    <option key={route.id} value={route.id}>
                      {route.title}{route.distance_km ? ` (${route.distance_km} km)` : ""}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          {routeId && navData === null && (
            <div className="rounded-2xl border border-svj-border bg-svj-card p-6 text-sm text-slate-400">
              Deze route heeft geen navigatiegegevens, dus is er geen kaart om verkeersposten op te plaatsen.
            </div>
          )}

          {routeId && navData && (
            <div className="rounded-2xl border border-svj-border bg-svj-card overflow-hidden">
              <div className="px-4 py-2 border-b border-svj-border flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={detectCrossings}
                  disabled={suggestBusy}
                  data-testid="traffic-detect-crossings"
                  className="h-9 px-3 rounded-lg border border-svj-border text-slate-300 hover:text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-40"
                >
                  {suggestBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  Detecteer kruisingen
                </button>
                {routes.length > 1 && (
                  <button
                    type="button"
                    onClick={detectCrossingsForDay}
                    disabled={suggestDayBusy}
                    data-testid="traffic-detect-crossings-day"
                    title="Voert dezelfde detectie uit op elke route van deze dag (bv. 5 km en 10 km tegelijk)"
                    className="h-9 px-3 rounded-lg border border-svj-border text-slate-300 hover:text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-40"
                  >
                    {suggestDayBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                    Genereer posten voor deze dag (alle afstanden)
                  </button>
                )}
                <p className="text-[11px] text-slate-500">
                  Klik op de kaart om een nieuwe post te plaatsen, sleep een post om te verplaatsen, of klik op een
                  bestaande post om te bewerken, toe te wijzen of te verwijderen. "Detecteer kruisingen" combineert
                  twee bronnen: scherpe bochten/zelfkruisingen in de routevorm zelf, én (indien live wegdata
                  beschikbaar is) echte straatkruisingen uit OpenStreetMap — ook als de route daar gewoon rechtdoor
                  loopt. Nooit automatisch definitief: elke suggestie moet zelf bevestigd, verplaatst of verwijderd
                  worden. Een suggestie die ook op een andere route van deze dag voorkomt (bv. dezelfde kruising op
                  zowel de 5 als de 10 km) wordt in de titel gemarkeerd.
                </p>
              </div>
              <div className="relative h-[480px]">
                <div className="absolute inset-0">
                  <div ref={mapContainerRef} className="w-full h-full" data-testid="admin-traffic-control-map" />
                </div>
                {mapError && (
                  <div className="absolute inset-0 flex items-center justify-center bg-svj-bg p-3">
                    <div className="flex items-start gap-2 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 max-w-sm">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {mapError}
                    </div>
                  </div>
                )}

                {editorPost && (
                  <div
                    className="absolute top-2 right-2 bottom-2 w-72 max-w-[85%] bg-svj-card border border-svj-border rounded-xl shadow-xl flex flex-col overflow-hidden z-10"
                    data-testid="traffic-post-editor"
                  >
                    <div className="px-3 py-2 border-b border-svj-border flex items-center justify-between shrink-0">
                      <span className="text-xs font-bold text-white">
                        {editorPost.id ? "Verkeerspost bewerken" : "Nieuwe verkeerspost"}
                      </span>
                      <button type="button" onClick={closeEditor} aria-label="Sluiten" className="text-slate-400 hover:text-white">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-3 space-y-3 text-xs">
                      {editorPost.status === "suggested" && (
                        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-2 text-[11px] text-amber-300">
                          Dit is een automatisch gesuggereerde post — nog niet bevestigd.
                        </div>
                      )}
                      <div>
                        <label className="block text-slate-400 mb-1">Titel</label>
                        <input
                          value={editorPost.title}
                          onChange={(e) => setEditorPost((prev) => ({ ...prev, title: e.target.value }))}
                          data-testid="traffic-post-title"
                          className="w-full h-8 px-2 rounded-lg bg-svj-bg border border-svj-border text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-400 mb-1">Instructies voor deze post</label>
                        <textarea
                          value={editorPost.notes || ""}
                          onChange={(e) => setEditorPost((prev) => ({ ...prev, notes: e.target.value }))}
                          rows={2}
                          placeholder="Bijv. 'Sta bij de kruising en dirigeer verkeer naar de parkeerplaats'"
                          data-testid="traffic-post-notes"
                          className="w-full px-2 py-1.5 rounded-lg bg-svj-bg border border-svj-border text-white resize-none"
                        />
                        <p className="mt-1 text-[11px] text-slate-500">
                          Wordt getoond aan de regelaar in StapKompas Verkeer, ook in de melding wanneer de sluiter
                          dichtbij komt.
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <label className="block text-slate-400 mb-1">Actief vanaf</label>
                          <input
                            type="time"
                            value={editorPost.active_from || ""}
                            onChange={(e) => setEditorPost((prev) => ({ ...prev, active_from: e.target.value || "" }))}
                            data-testid="traffic-post-active-from"
                            className="w-full h-8 px-2 rounded-lg bg-svj-bg border border-svj-border text-white"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-slate-400 mb-1">Actief tot</label>
                          <input
                            type="time"
                            value={editorPost.active_until || ""}
                            onChange={(e) => setEditorPost((prev) => ({ ...prev, active_until: e.target.value || "" }))}
                            data-testid="traffic-post-active-until"
                            className="w-full h-8 px-2 rounded-lg bg-svj-bg border border-svj-border text-white"
                          />
                        </div>
                      </div>
                      <p className="text-[11px] text-slate-500 -mt-2">
                        Leeg = altijd actief. Alleen een waarschuwing bij overlap met een andere post, geen blokkade.
                      </p>
                      <div>
                        <label className="block text-slate-400 mb-1">Foto (taakinstructie)</label>
                        {editorPost.image_url ? (
                          <div className="flex items-center gap-2">
                            <img
                              src={editorPost.image_url}
                              alt="Verkeerspost"
                              className="h-14 w-14 rounded-lg object-cover border border-svj-border"
                            />
                            <button
                              type="button"
                              onClick={removePostImage}
                              disabled={postImageUploading}
                              data-testid="traffic-post-image-remove"
                              className="text-red-400 hover:text-red-300 text-[11px] font-semibold disabled:opacity-40"
                            >
                              Verwijderen
                            </button>
                          </div>
                        ) : editorPost.id ? (
                          <label className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-svj-cyan hover:text-white cursor-pointer">
                            <ImageIcon className="h-3.5 w-3.5" />
                            {postImageUploading ? "Uploaden…" : "Foto toevoegen"}
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp"
                              onChange={(e) => uploadPostImage(e.target.files?.[0])}
                              disabled={postImageUploading}
                              data-testid="traffic-post-image-upload"
                              className="hidden"
                            />
                          </label>
                        ) : (
                          <p className="text-[11px] text-slate-500">Eerst opslaan, dan kan een foto toegevoegd worden.</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-slate-400 mb-1">Toegewezen regelaar</label>
                        <select
                          value={editorPost.assigned_controller_id || ""}
                          onChange={(e) =>
                            setEditorPost((prev) => ({ ...prev, assigned_controller_id: e.target.value || null }))
                          }
                          data-testid="traffic-post-assign-select"
                          className="w-full h-8 px-2 rounded-lg bg-svj-bg border border-svj-border text-white"
                        >
                          <option value="">— Niet toegewezen —</option>
                          {controllers
                            .filter((c) => c.role === "controller")
                            .map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.display_name}
                                {c.active === false ? " (inactief)" : ""}
                              </option>
                            ))}
                        </select>
                      </div>
                      {editorPost.distance_along_route_meters != null && (
                        <p className="text-[10px] text-slate-500">
                          Afstand langs route: {Math.round(editorPost.distance_along_route_meters)} m
                        </p>
                      )}
                    </div>
                    <div className="p-3 border-t border-svj-border flex items-center justify-between gap-2 shrink-0">
                      {editorPost.id ? (
                        <button
                          type="button"
                          onClick={deleteEditor}
                          disabled={postSaving}
                          data-testid="traffic-post-delete"
                          className="text-red-400 hover:text-red-300 text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-40"
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Verwijderen
                        </button>
                      ) : (
                        <span />
                      )}
                      <div className="flex items-center gap-2">
                        {editorPost.status === "suggested" && (
                          <button
                            type="button"
                            onClick={confirmSuggestion}
                            disabled={postSaving}
                            data-testid="traffic-post-confirm"
                            className="rounded-lg border border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-3 py-1.5 disabled:opacity-40"
                          >
                            Bevestigen
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={saveEditor}
                          disabled={postSaving || !editorPost.title.trim()}
                          data-testid="traffic-post-save"
                          className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-4 py-1.5 disabled:opacity-40"
                        >
                          {postSaving ? "Opslaan…" : "Opslaan"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="p-4 border-t border-svj-border text-xs text-slate-400 space-y-1">
                {sluiter?.available ? (
                  <span data-testid="traffic-control-sluiter-status">
                    Sluiter actief — op ca.{" "}
                    {sluiter.distance_along_route_meters != null ? Math.round(sluiter.distance_along_route_meters) : "?"} m
                    langs de route.
                  </span>
                ) : (
                  <span data-testid="traffic-control-sluiter-status">Nog geen live sluiter-positie.</span>
                )}
                {sluiter?.available && sluiter.age_seconds > SLUITER_STALE_WARNING_SECONDS && (
                  <div
                    className="flex items-center gap-1.5 text-amber-300"
                    data-testid="traffic-control-sluiter-stale-warning"
                  >
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    <span>
                      Positie is {Math.round(sluiter.age_seconds)} sec oud — mogelijk geen GPS/verbinding meer bij de
                      sluiter.
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {routeId && trafficPosts.length > 0 && (
            <div className="rounded-2xl border border-svj-border bg-svj-card p-5">
              <div className="flex flex-wrap items-end justify-between gap-2 mb-3">
                <div>
                  <h2 className="text-sm font-bold text-white mb-1">Posten op deze route</h2>
                  <p className="text-xs text-slate-400">
                    Snel een andere regelaar toewijzen zonder de kaart te openen. Klik op een post hierboven op de
                    kaart voor de volledige bewerking (titel, instructies, tijdvenster, foto, verwijderen).
                  </p>
                </div>
                <div className="flex items-end gap-2">
                  <label className="block">
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                      Dupliceer posten naar
                    </span>
                    <select
                      value={duplicateTargetRouteId}
                      onChange={(e) => setDuplicateTargetRouteId(e.target.value)}
                      data-testid="traffic-duplicate-target-route"
                      className="mt-1 h-9 px-2 rounded-lg bg-svj-bg border border-svj-border text-white text-sm min-w-[160px]"
                    >
                      <option value="">— Kies route —</option>
                      {allRoutesForDuplicate.map((r) => (
                        <option key={r.id} value={r.id}>{r.title}</option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    onClick={duplicatePosts}
                    disabled={!duplicateTargetRouteId || duplicating}
                    data-testid="traffic-duplicate-posts"
                    className="h-9 px-3 rounded-lg border border-svj-border text-slate-300 hover:text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-40"
                  >
                    {duplicating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CopyPlus className="h-3.5 w-3.5" />}
                    Dupliceren
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                {trafficPosts.map((post) => (
                  <div
                    key={post.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-svj-border bg-svj-bg px-3 py-2"
                    data-testid={`traffic-post-assign-row-${post.id}`}
                  >
                    <div className="text-sm text-white">
                      {post.title}
                      {post.status === "suggested" && (
                        <span className="ml-2 text-[10px] uppercase tracking-widest text-amber-400">Suggestie</span>
                      )}
                    </div>
                    <select
                      value={post.assigned_controller_id || ""}
                      onChange={(e) => assignPostController(post, e.target.value)}
                      data-testid={`traffic-post-assign-select-${post.id}`}
                      className="h-8 px-2 rounded-lg bg-svj-card border border-svj-border text-white text-sm"
                    >
                      <option value="">— Niet toegewezen —</option>
                      {controllers
                        .filter((c) => c.role === "controller")
                        .map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.display_name}
                            {c.active === false ? " (inactief)" : ""}
                          </option>
                        ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
